import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import ChevronDown from '@atlaskit/icon/core/chevron-down';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { layers } from '@atlaskit/theme/constants';
import Button from '@atlaskit/button/standard-button';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { COVERPICTUREWIDTH } from './contentTopperConstants';

const i18n = defineMessages({
	fixedWidthSelectOption: {
		id: 'content-topper.page-title.popup.fixed-width.option',
		defaultMessage: 'Fixed width',
		description: 'Text for select dropdown option to control header image width to a smaller size',
	},
	fullWidthSelectOption: {
		id: 'content-topper.page-title.popup.full-width.option',
		defaultMessage: 'Full width',
		description: 'Text for select dropdown option to control header image width to a larger size',
	},
});

type FullWidthDropdownMenuProps = {
	userOffline?: boolean;
	onImageWidthChange: (coverPictureWidth: COVERPICTUREWIDTH) => void;
	selectedWidth: COVERPICTUREWIDTH;
	onCoverImageWidthDropdownOptionSelected?: (isCoverImageWidthChanged: boolean) => void;
};

export const FullWidthDropdownMenu = ({
	userOffline,
	onImageWidthChange,
	selectedWidth = COVERPICTUREWIDTH.FULL,
	onCoverImageWidthDropdownOptionSelected,
}: FullWidthDropdownMenuProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const createWidthToggleDropdownAnalytics = useCallback(
		(coverPictureWidth: COVERPICTUREWIDTH) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					source: `widthToggleDropdownButton`,
					actionSubject: 'button',
					actionSubjectId: `${coverPictureWidth}WidthToggleDropdownButton`,
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	return (
		<DropdownMenu
			placement="bottom-start"
			zIndex={layers.dialog()}
			shouldRenderToParent
			shouldFitContainer
			trigger={({ triggerRef, ...props }) => (
				<Button
					{...props}
					iconAfter={<ChevronDown label="" size="small" />}
					ref={triggerRef}
					shouldFitContainer
					// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides, @atlaskit/ui-styling-standard/enforce-style-prop
					style={{ textAlign: 'left' }}
					isDisabled={userOffline}
				>
					{selectedWidth == COVERPICTUREWIDTH.FULL ? (
						<FormattedMessage {...i18n.fullWidthSelectOption} />
					) : (
						<FormattedMessage {...i18n.fixedWidthSelectOption} />
					)}
				</Button>
			)}
		>
			<DropdownItemGroup>
				<DropdownItem
					key={COVERPICTUREWIDTH.FULL}
					onClick={() => {
						onImageWidthChange(COVERPICTUREWIDTH.FULL);
						createWidthToggleDropdownAnalytics(COVERPICTUREWIDTH.FULL);
						onCoverImageWidthDropdownOptionSelected?.(selectedWidth !== COVERPICTUREWIDTH.FULL);
					}}
					testId="coverPictureFullWidthToggleDropdownItem"
				>
					<FormattedMessage {...i18n.fullWidthSelectOption} />
				</DropdownItem>
				<DropdownItem
					key={COVERPICTUREWIDTH.FIXED}
					onClick={() => {
						onImageWidthChange(COVERPICTUREWIDTH.FIXED);
						createWidthToggleDropdownAnalytics(COVERPICTUREWIDTH.FIXED);
						onCoverImageWidthDropdownOptionSelected?.(selectedWidth !== COVERPICTUREWIDTH.FIXED);
					}}
					testId="coverPictureFixedWidthToggleDropdownItem"
				>
					<FormattedMessage {...i18n.fixedWidthSelectOption} />
				</DropdownItem>
			</DropdownItemGroup>
		</DropdownMenu>
	);
};
